import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/Button'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import {
  createPoleScheduleItem,
  extractApiErrorMessage,
  updatePoleScheduleItem,
} from '@/services/events'
import { uploadEventScheduleItemAssets } from '@/services/upload-event-schedule-item-assets'
import {
  EventScheduleItemProps,
  ScheduleItemMutationPayloadProps,
  ScheduleItemType,
} from '@/types/eventsProps'

interface FormEventScheduleItemProps {
  eventId: string
  poleId: string
  eventSlug: string
  scheduleItem?: EventScheduleItemProps
  onSaved: () => Promise<void> | void
}

interface ScheduleItemFormValues {
  type: ScheduleItemType
  title: string
  description: string
  startsAt: string
  endsAt: string
  officialUrl: string
  sortOrder: string
}

const scheduleItemTypeOptions: Array<{
  label: string
  value: ScheduleItemType
}> = [
  { label: 'Transmissao de jogo', value: 'GAME_SCREENING' },
  { label: 'Cerimonia', value: 'CEREMONY' },
  { label: 'Abertura', value: 'OPENING' },
  { label: 'Atividade cultural', value: 'CULTURAL_ACTIVITY' },
  { label: 'Ativacao', value: 'ACTIVATION' },
  { label: 'DJ', value: 'DJ' },
  { label: 'Informacao', value: 'INFO' },
  { label: 'Outro', value: 'OTHER' },
]

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const timezoneOffset = parsedDate.getTimezoneOffset()
  const localDate = new Date(parsedDate.getTime() - timezoneOffset * 60000)
  return localDate.toISOString().slice(0, 16)
}

function toIsoDateTime(value: string) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  return parsedDate.toISOString()
}

function toOptionalNumber(value: string) {
  if (!value.trim()) return undefined

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function FormEventScheduleItem({
  eventId,
  poleId,
  eventSlug,
  scheduleItem,
  onSaved,
}: FormEventScheduleItemProps) {
  const { closeModal } = useModal()
  const { showToast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [bannerFile, setBannerFile] = useState<File | null>(null)
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(
    scheduleItem?.coverImageUrl ?? null,
  )
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(
    scheduleItem?.bannerImageUrl ?? null,
  )

  const { register, handleSubmit, setValue } = useForm<ScheduleItemFormValues>({
    defaultValues: {
      type: 'OTHER',
      title: '',
      description: '',
      startsAt: '',
      endsAt: '',
      officialUrl: '',
      sortOrder: '',
    },
  })

  const onSubmit = async (data: ScheduleItemFormValues) => {
    const startsAt = toIsoDateTime(data.startsAt)
    const endsAt = data.endsAt ? toIsoDateTime(data.endsAt) : ''

    if (!data.title.trim() || !startsAt) {
      showToast('Tipo, titulo e inicio do item sao obrigatorios.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    if (endsAt && new Date(endsAt).getTime() < new Date(startsAt).getTime()) {
      showToast('A data final nao pode ser anterior ao inicio.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    try {
      setIsLoading(true)

      const uploadedFiles =
        coverFile || bannerFile
          ? await uploadEventScheduleItemAssets({
              coverFile: coverFile ?? undefined,
              bannerFile: bannerFile ?? undefined,
              eventSlug,
              itemTitle: data.title,
            })
          : null

      const payload: ScheduleItemMutationPayloadProps = {
        type: data.type,
        title: data.title,
        description: data.description,
        startsAt,
        endsAt: endsAt || undefined,
        officialUrl: data.officialUrl,
        sortOrder: toOptionalNumber(data.sortOrder),
        coverImageUrl:
          uploadedFiles?.coverFile?.url ??
          scheduleItem?.coverImageUrl ??
          undefined,
        bannerImageUrl:
          uploadedFiles?.bannerFile?.url ??
          scheduleItem?.bannerImageUrl ??
          undefined,
      }

      if (scheduleItem?.id) {
        await updatePoleScheduleItem(eventId, poleId, scheduleItem.id, payload)
        showToast('Schedule item atualizado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      } else {
        await createPoleScheduleItem(eventId, poleId, payload)
        showToast('Schedule item criado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      }

      await Promise.resolve(onSaved())
      closeModal()
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          'Nao foi possivel salvar o schedule item.',
        ),
        {
          type: 'error',
          theme: 'light',
        },
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!scheduleItem) return

    setValue('type', scheduleItem.type)
    setValue('title', scheduleItem.title ?? '')
    setValue('description', scheduleItem.description ?? '')
    setValue('startsAt', toDateTimeLocal(scheduleItem.startsAt))
    setValue('endsAt', toDateTimeLocal(scheduleItem.endsAt))
    setValue('officialUrl', scheduleItem.officialUrl ?? '')
    setValue(
      'sortOrder',
      scheduleItem.sortOrder !== null ? `${scheduleItem.sortOrder ?? ''}` : '',
    )
  }, [scheduleItem, setValue])

  useEffect(() => {
    if (!coverFile) {
      setCoverPreviewUrl(scheduleItem?.coverImageUrl ?? null)
      return
    }

    const objectUrl = URL.createObjectURL(coverFile)
    setCoverPreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [coverFile, scheduleItem?.coverImageUrl])

  useEffect(() => {
    if (!bannerFile) {
      setBannerPreviewUrl(scheduleItem?.bannerImageUrl ?? null)
      return
    }

    const objectUrl = URL.createObjectURL(bannerFile)
    setBannerPreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [bannerFile, scheduleItem?.bannerImageUrl])

  const handleFileChange =
    (setter: (file: File | null) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(event.currentTarget.files?.[0] ?? null)
    }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Tipo</h4>
          <select
            {...register('type', { required: true })}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          >
            {scheduleItemTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <h4 className="mb-2 text-sm font-bold">Ordem</h4>
          <input
            {...register('sortOrder')}
            type="number"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">Titulo</h4>
        <input
          {...register('title', { required: true })}
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          placeholder="Abertura oficial"
        />
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">Descricao</h4>
        <textarea
          {...register('description')}
          rows={3}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          placeholder="Detalhes editoriais do item"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Inicio</h4>
          <input
            {...register('startsAt', { required: true })}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>

        <div>
          <h4 className="mb-2 text-sm font-bold">Fim</h4>
          <input
            {...register('endsAt')}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">URL oficial</h4>
        <input
          {...register('officialUrl')}
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          placeholder="https://evento.com.br/programacao/abertura"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Capa</h4>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange(setCoverFile)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
          {coverPreviewUrl && (
            <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-2">
              <img
                src={coverPreviewUrl}
                alt="cover preview"
                className="h-28 w-full rounded object-cover"
              />
            </div>
          )}
        </div>

        <div>
          <h4 className="mb-2 text-sm font-bold">Banner</h4>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange(setBannerFile)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
          {bannerPreviewUrl && (
            <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-2">
              <img
                src={bannerPreviewUrl}
                alt="banner preview"
                className="h-28 w-full rounded object-cover"
              />
            </div>
          )}
        </div>
      </div>

      <Button
        title={scheduleItem ? 'Atualizar schedule item' : 'Criar schedule item'}
        variant="purple"
        isLoading={isLoading}
      />
    </form>
  )
}
